/** @jsx jsx */
import { Global, css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { DropDown, color } from '@coursera/coursera-ui';
import { SvgChevronDown } from '@coursera/coursera-ui/svg';

import { getLabelForRuling } from 'bundles/author-assignment-grading/utils/plagiarismFlagUtils';
import type { CheatingCaseRuling } from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';
import { CHEATING_CASE_RULINGS } from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';

import _t from 'i18n!nls/academic-integrity';

type Props = {
  ruling?: CheatingCaseRuling;
  onSelect: (ruling: CheatingCaseRuling) => void;
  readOnly: boolean;
  disabled: boolean;
  vivaExamEnabled: boolean;
};

const styles = {
  plagiarismStatusDropdown: css`
    flex: none;
  `,
  flagStatusVivaEnabled: css`
    margin-top: 16px;
  `,
  boldFlagStatus: css`
    font-weight: bold;
    font-size: 0.75rem;
    font-family: 'Open Sans', sans-serif;
  `,
  commentText: css`
    margin-top: 8px;
    margin-bottom: 16px;
  `,
  statusDropdownTrigger: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: white;
    width: 242px;
    height: 45px;
    font-size: 0.875rem;
    font-family: 'Open Sans', sans-serif;
    margin-bottom: 24px;
    margin-top: 6px;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);

    &:focus,
    &:hover {
      border: 1px solid var(--cds-color-blue-800);
    }

    &:disabled {
      background-color: var(--cds-color-neutral-disabled-weak);
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
  statusDropdown: css`
    width: 242px;
  `,
  globalStyles: css`
    .ReactModalPortal .ReactModal__Content.ReactModal__Content--after-open {
      z-index: 100000;
    }
  `,
};

/**
 * Dropdown which allows users to update the resolution status(ruling) of a plagiarism case
 */

export default class PlagiarismStatusDropDown extends React.Component<Props> {
  handleClickMenuItem = (e: React.SyntheticEvent<HTMLButtonElement>, value: CheatingCaseRuling) => {
    const { onSelect } = this.props;
    onSelect(value);
  };

  listItemData = [
    {
      label: getLabelForRuling(CHEATING_CASE_RULINGS.UNRESOLVED),
      value: CHEATING_CASE_RULINGS.UNRESOLVED,
    },
    {
      label: getLabelForRuling(CHEATING_CASE_RULINGS.RESOLVED_NO_CHEATING),
      value: CHEATING_CASE_RULINGS.RESOLVED_NO_CHEATING,
    },
    {
      label: getLabelForRuling(CHEATING_CASE_RULINGS.RESOLVED_CHEATING),
      value: CHEATING_CASE_RULINGS.RESOLVED_CHEATING,
    },
  ];

  render() {
    const { ruling, readOnly, disabled, vivaExamEnabled } = this.props;
    let selectedIndex = this.listItemData.findIndex((el) => el.value === ruling);
    selectedIndex = selectedIndex === -1 ? 0 : selectedIndex;

    return (
      <div css={styles.plagiarismStatusDropdown}>
        <Global styles={styles.globalStyles} />
        <Typography2
          variant="subtitleMedium"
          component="label"
          css={vivaExamEnabled ? styles.flagStatusVivaEnabled : styles.boldFlagStatus}
        >
          {_t('Flag Status')}
        </Typography2>
        {vivaExamEnabled && (
          <Typography2 variant="bodySecondary" component="p" css={styles.commentText}>
            {_t('Add a comment first to update the plagiarism flag status.')}
          </Typography2>
        )}
        {!readOnly && (
          <DropDown.ButtonMenu
            rootClassName="status-dropdown"
            css={styles.statusDropdown}
            renderButton={({ getDropDownButtonProps }) => (
              <button
                disabled={disabled}
                type="button"
                {...getDropDownButtonProps()}
                css={styles.statusDropdownTrigger}
              >
                {this.listItemData[selectedIndex].label} <SvgChevronDown size={14} color={color.primary} />
              </button>
            )}
            listItemData={this.listItemData}
            onClick={this.handleClickMenuItem}
            currentSelectedIndex={selectedIndex}
            showBorder={true}
          />
        )}

        {readOnly && (
          <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
            {getLabelForRuling(ruling)}
          </Typography2>
        )}
      </div>
    );
  }
}
